import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard, ShoppingCart, Cloud, Users, Ticket, Tag,
  UserCog, LogOut, Menu, X, Shield, ChevronDown, MessageSquare, MailCheck, Settings, CreditCard
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout, hasPermission, loading } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!loading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Laden...</div>
      </div>
    );
  }

  if (!admin) {
    return null;
  }

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', permission: 'dashboard:view' },
    { icon: ShoppingCart, label: 'Bestellungen', path: '/admin/orders', permission: 'orders:view' },
    { icon: CreditCard, label: 'Abonnements', path: '/admin/subscriptions', permission: 'orders:view' },
    { icon: Cloud, label: 'Clouds', path: '/admin/clouds', permission: 'clouds:view' },
    { icon: Users, label: 'Kunden', path: '/admin/customers', permission: 'customers:view' },
    { icon: MessageSquare, label: 'Support', path: '/admin/tickets', permission: 'support:view' },
    { icon: MailCheck, label: 'E-Mail Log', path: '/admin/mail-log', permission: 'support:view' },
    { icon: Tag, label: 'Gutscheine', path: '/admin/coupons', permission: 'coupons:view' },
    { icon: UserCog, label: 'Admin-Verwaltung', path: '/admin/admins', permission: 'admin:view' },
  ];

  const filteredNavItems = navItems.filter(item => hasPermission(item.permission));

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-layout">
      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-slate-800 border-b border-slate-700 z-50">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Link to="/admin" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-blue-500" />
              <span className="text-xl font-semibold text-white hidden sm:block">Wolkenhelden Admin</span>
            </Link>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-700">
                <span className="mr-2">{admin.name}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
              <div className="px-2 py-1.5">
                <p className="text-sm text-white font-medium">{admin.name}</p>
                <p className="text-xs text-slate-400">{admin.email}</p>
                <p className="text-xs text-blue-400 mt-1 capitalize">{admin.role.replace('_', ' ')}</p>
              </div>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem
                onClick={() => navigate('/admin/profile')}
                className="text-slate-300 focus:text-white focus:bg-slate-700 cursor-pointer"
              >
                <Settings className="h-4 w-4 mr-2" />
                Profil bearbeiten
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-400 focus:text-red-300 focus:bg-slate-700 cursor-pointer"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-16 left-0 bottom-0 w-64 bg-slate-800 border-r border-slate-700
        transform transition-transform duration-200 z-40
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <nav className="p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${active
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }
                `}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
